import { isNil } from "lodash-es";
import { ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configIdType,
  configPageableMetaType,
  configSystemRecordType,
  IId,
  IPageable,
  IPartialPageable,
  ISystemRecord,
} from "@/core/api";
import { dateType, TDate } from "@/core/dates";
import { attachmentType } from "@/data/attachments";
import { IJobApplication, jobApplicationType } from "@/data/job-applications";
import { configJobRequestType, IJobRequest } from "@/data/job-requests";
import { IWorkOrder, workOrderType } from "@/data/work-orders";

export type AssigneeStatus = "NOT_REGISTERED" | "EMAIL_NOT_CONFIRMED" | "CONFIRMED" | "DEACTIVATED" | "DELETED";
export interface ITaskAssignee extends IId {
  createdDate: TDate;
  email: string;
  firstName: string;
  lastName: string;
  modifiedDate: TDate;
  status: AssigneeStatus;
}

const configTaskAssigneeType: TType<ITaskAssignee> = {
  ...configIdType,
  createdDate: dateType(),
  email: stringType(),
  firstName: stringType(),
  lastName: stringType(),
  modifiedDate: dateType(),
  status: stringType(),
};

export const taskAssigneeType = ensure(configTaskAssigneeType);

export type TaskPriority = "CRITICAL" | "URGENT" | "PENDING";
export type TaskType = "WO_CONFIRM" | "JR_DESIGNATED_CANDIDATE_CREATE";

export interface IGenericTask<T extends TaskType, E> extends IId {
  assignee?: ITaskAssignee;
  createdDate: TDate;
  criticalDeadline: TDate;
  entity: E;
  entityId: number;
  entityType: string;
  modifiedDate: TDate;
  priority: TaskPriority;
  processType: string;
  type: T;
  urgentDeadline: TDate;
  externalAttachments: [];
}

export interface IWOConfirmTask extends IGenericTask<"WO_CONFIRM", IWorkOrder> {}

export interface ITaskJobRequest extends IJobRequest {
  applications: IJobApplication[];
}

const configTaskJobRequestType = {
  ...configJobRequestType,
  applications: ensureArray(jobApplicationType),
};

const taskJobRequestType = ensure(configTaskJobRequestType);

export interface IDCCreateTask extends IGenericTask<"JR_DESIGNATED_CANDIDATE_CREATE", ITaskJobRequest> {}

export type ITask = IWOConfirmTask | IDCCreateTask;

const configTaskType: TType<ITask> = {
  ...configIdType,
  assignee: v => (isNil(v) ? null : taskAssigneeType(v)),
  createdDate: dateType(),
  criticalDeadline: dateType(),
  entity: (property, task) => (task?.type === "WO_CONFIRM" ? workOrderType(property) : taskJobRequestType(property)),
  entityId: numberType(),
  entityType: stringType(),
  modifiedDate: dateType(),
  priority: stringType(),
  processType: stringType(),
  type: stringType(),
  urgentDeadline: dateType(),
  externalAttachments: ensureArray(attachmentType),
};

export const taskType = ensure(configTaskType);

export interface IPageableTask extends IPageable<ITask[]> {}

export const pageableTaskTypeConfig: TType<IPageableTask> = {
  ...configPageableMetaType,
  content: ensureArray(taskType),
};

export const pageableTaskType = ensure(pageableTaskTypeConfig);

export interface IPartialPageableTask extends IPartialPageable<ITask[]> {}

export type WOConfirmTaskActionType = "accept" | "reject";
export type DCCreateTaskActionType = "cancel";

export type TaskActionType = WOConfirmTaskActionType | DCCreateTaskActionType;

export interface ITaskAction<T extends TaskActionType> {
  action: T;
  comment?: string;
}

export type WOConfirmTaskAction = ITaskAction<WOConfirmTaskActionType>;
export type DCCreateTaskAction = ITaskAction<DCCreateTaskActionType>;

export type TaskAction = WOConfirmTaskAction | DCCreateTaskAction;

const taskActionConfig: TType<TaskAction> = {
  action: stringType(),
  comment: stringType(),
};

export const taskActionType = ensure(taskActionConfig);

export interface ITaskCount {
  count: number;
  criticalTasks: number;
  pendingTasks: number;
  unassignedTasks: number;
  urgentTasks: number;
}

const taskCountConfig: TType<ITaskCount> = {
  count: numberType(0),
  criticalTasks: numberType(0),
  pendingTasks: numberType(0),
  unassignedTasks: numberType(0),
  urgentTasks: numberType(0),
};

export const taskCountType = ensure(taskCountConfig);

interface ITaskOriginServiceDetailsBase {
  color: string;
  homePageLink: string;
  logo: string;
  vatNumber: string;
}

const configTaskOriginServiceDetailsType: TType<ITaskOriginServiceDetailsBase> = {
  color: stringType(""),
  homePageLink: stringType(""),
  logo: stringType(""),
  vatNumber: stringType(""),
};

export const taskOriginServiceDetailsType = ensure(configTaskOriginServiceDetailsType);

interface ITaskOriginServiceBase extends IId, ISystemRecord {
  name: string;
}

const configTaskOriginServiceBaseType: TType<ITaskOriginServiceBase> = {
  ...configSystemRecordType,
  ...configIdType,
  name: stringType(""),
};

export const taskOriginServiceBaseType = ensure(configTaskOriginServiceBaseType);

export interface ITaskOriginService extends ITaskOriginServiceBase {
  details: ITaskOriginServiceDetailsBase;
  parent: ITaskOriginServiceBase;
}

const configTaskOriginServiceType: TType<ITaskOriginService> = {
  ...configTaskOriginServiceBaseType,
  details: taskOriginServiceDetailsType,
  parent: taskOriginServiceBaseType,
};

export const taskOriginServiceType = ensure(configTaskOriginServiceType);
